import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getTaskOverview,
  listTemplates,
  taskOverviewKey,
  type OverviewScope,
  type OverviewTile,
  type TaskFilter,
} from '../api';
import { QueryState } from './QueryState';
import { latePileSentence } from '../taskVocab';

/**
 * The shape of the work, above the list of it.
 *
 * All three mockup directions put these three summaries at the top of the Tasks screen and the
 * screen has never had any of them: it opened on a flat list with a search box, so the only way
 * to learn "how much is in progress" was to filter and count the rows by eye.
 *
 * The counts are buttons rather than text. A number that tells you 14 things are in the inbox and
 * then makes you go and set a filter to see them is a decoration; the same number as a filter is
 * navigation, and it costs nothing extra to build.
 */

/**
 * What each tile is called. What each tile *means* is not written here.
 *
 * The filter comes back from `/api/tasks/overview` beside the number, and is applied verbatim —
 * see `TaskOverview.filters`. This list held a filter per tile and one of them asked a different
 * question from the count printed on it: `Inbox` counted `status = 'inbox'` and filtered by
 * `inbox: true`, the contract's *unsorted capture* predicate, so a chip reading "30 inbox" opened
 * 52 rows. Two predicates, one noun, one screen.
 *
 * "Inbox" here is the status — the same word the board's Inbox column, the status filter and
 * `GET /api/tasks?status=inbox` use. Unsorted capture keeps its own filter in the panel below,
 * under its own name.
 */
const TILE_LABELS: { key: OverviewTile; label: string }[] = [
  /*
    First, and the only one with a tone of its own.

    The screen graded every row as late — `dueLook` draws the `Nd late` pill — and counted
    everything except that, so the one number a manager opens Tasks for was the one number not on
    it: twenty-six late rows on the screenshot this was reported from, learnable only by scrolling
    3300px counting red.

    The word is *late*, matching the pills, where the deadline filter panel says "Overdue". Two
    words for one predicate on one screen is the smaller problem; the chip takes the word the rows
    already use.
  */
  { key: 'late', label: 'Late' },
  { key: 'inProgress', label: 'In progress' },
  { key: 'next', label: 'Next' },
  { key: 'inbox', label: 'Inbox' },
  // Derived rather than stored, so this cannot filter by status — it filters by the derived
  // state instead, which `listTasks` gained in Phase 28. Until then the tile was a dead end.
  { key: 'blocked', label: 'Blocked' },
  /*
    The server's day, not the browser's.

    This tile used to build its own `scheduledDate` from the profile's timezone in the client, one
    fix after building it from the browser's — and both were the same mistake as the inbox chip,
    a filter derived a second time from something that had already decided it. The count is taken
    in the caller's zone; the day it was taken with now travels with it.
  */
  { key: 'plannedToday', label: 'Planned today' },
];

/**
 * The shelf, kept out of `TILE_LABELS` on purpose.
 *
 * Those are open work, and every rule written over them reads "open": "Nothing open." when all of
 * them are zero, the "…: none." sentence naming the ones that are, the zero tiles drawn when
 * nothing is live. Parked is none of those — counting it in "Nothing open." would make that
 * sentence false on the minimalist's account, and listing "parked: none" on every account that has
 * never parked anything is the chrome the minimalist reports first. So it is drawn after them,
 * muted, and only when it is not zero (`01a1774e-16c4`).
 */
const SHELF_LABEL = 'Parked';

/**
 * Whether the list is already showing what a tile counts.
 *
 * Every field the tile's filter names, holding the value it names. Not equality of the two
 * filters: a press on *next* with a label filter already on is still the *next* chip pressed, and
 * the list carries fields — `sort`, `q` — that no tile ever names. The values are all scalars (a
 * status, a day, a boolean), so `===` is the whole comparison.
 *
 * `late` is matched like the rest, and that is only sound because the list writes `filter.late`
 * from its Deadline choice and from nowhere else — see `pressOverview` in `TaskList`.
 */
export function tileIsPressed(tile: TaskFilter, active: TaskFilter): boolean {
  return Object.entries(tile).every(([key, value]) => active[key as keyof TaskFilter] === value);
}

function Tile({
  label,
  value,
  hint,
  onClick,
  pressed,
}: {
  label: string;
  value: string;
  hint?: string;
  onClick?: () => void;
  /** A filtering tile is a toggle, and says which way it is — see the chips' `aria-pressed`. */
  pressed?: boolean;
}) {
  const body = (
    <>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </>
  );
  // A button only when it does something — a tile that looks pressable and is not is a lie.
  return onClick ? (
    <button
      type="button"
      className="stat-tile stat-tile-button"
      title={hint}
      aria-pressed={pressed}
      onClick={onClick}
    >
      {body}
    </button>
  ) : (
    <div className="stat-tile" title={hint}>
      {body}
    </div>
  );
}

export function TaskOverview({
  onFilter,
  active,
  scope,
}: {
  /**
   * A press on a count. The list decides what that does — set the tile's filter, or drop it when
   * the tile is already pressed — because only the list knows which of its controls owns each
   * field. This component used to have it written straight into the filter, and `late` is owned
   * by the Deadline choice, not by `filter`.
   */
  onFilter: (filter: TaskFilter) => void;
  /** The list's current filter, so each count can say whether it is the one being shown. */
  active: TaskFilter;
  /**
   * The scope of the list this sits above.
   *
   * Required rather than defaulted, because the default is the bug: these counts were always
   * `mine` while the list under them answered in whatever scope it remembered, so on *All* the
   * header read the caller's own numbers above the account's rows and `oldestLateDays` named the
   * worst of theirs. A count above a list is a claim about that list.
   */
  scope: OverviewScope;
}) {
  const overview = useQuery({
    queryKey: taskOverviewKey(scope),
    queryFn: () => getTaskOverview(scope),
  });
  // Already fetched on the Templates screen; the shared cache means this is usually free.
  const templates = useQuery({ queryKey: ['templates'], queryFn: listTemplates });
  /*
    Collapsed by default, and the collapsed form is one line.

    Twelve tiles across three sections used to open the Tasks screen, and on the owner's real
    account five of them read `0` or `—`: nothing in progress, nothing blocked, nothing planned
    today, no points anywhere. Measured, that pushed the first task row to 665 pixels down a
    720-pixel viewport — you opened the task list and saw one task.

    So the summary now leads with only the counts that are not zero. A zero is not information you
    need at a glance; it is information you need when you go looking, and "Show all" is where it
    lives. `largestOpen` is deliberately excluded from the summary line — it is a task title, so
    its width is unbounded.
  */
  const [expanded, setExpanded] = useState(false);

  // Same rule as everywhere else: a failed read must not read as an empty screen. The panel is
  // a summary of the list below it, and silently missing is how a summary comes to disagree.
  if (overview.isError) return <QueryState query={overview} what="the summary" />;
  if (!overview.data) return null;
  const { byStatus, complexity, filters, oldestLateDays, lateByDeadline, lateByPlan } =
    overview.data;

  /*
    Which templates actually carry the load.

    `useCount` has been returned by the API since Phase 4 and shown nowhere. Ranked and cut at
    four: the tail of a template list is templates made once and abandoned, and naming them is
    not information.
  */
  const used = (templates.data ?? [])
    .filter((t) => t.useCount > 0)
    .sort((a, b) => b.useCount - a.useCount)
    .slice(0, 4);

  const live = TILE_LABELS.filter((tile) => byStatus[tile.key] > 0);
  /* Whether this account puts sizes on anything at all — see the Complexity section below. */
  const estimatesAnything =
    complexity.openPoints > 0 ||
    complexity.donePointsThisWeek > 0 ||
    complexity.averagePoints !== null ||
    complexity.largestOpen !== null;

  if (!expanded) {
    return (
      <div className="task-overview is-collapsed" data-testid="task-overview">
        {live.length === 0 ? (
          <span className="subtext">Nothing open.</span>
        ) : (
          live.map((tile) => (
            <button
              key={tile.key}
              type="button"
              className={tile.key === 'late' ? 'count-chip is-late' : 'count-chip'}
              title={`Show ${tile.label.toLowerCase()}`}
              /* A toggle, and it says so: pressing the chip that is on turns it off. Without the
                 state a chip that had just filtered the list looked exactly like one that had not,
                 which is half of how `01a15a31-188b` went unnoticed. */
              aria-pressed={tileIsPressed(filters[tile.key], active)}
              onClick={() => onFilter(filters[tile.key])}
            >
              <strong>{byStatus[tile.key]}</strong> {tile.label.toLowerCase()}
            </button>
          ))
        )}
        {/* After the open counts and quieter than them — see `SHELF_LABEL`. */}
        {byStatus.parked > 0 && (
          <button
            type="button"
            className="count-chip is-shelf"
            data-testid="parked-chip"
            title="Show the parked tasks"
            aria-pressed={tileIsPressed(filters.parked, active)}
            onClick={() => onFilter(filters.parked)}
          >
            <strong>{byStatus.parked}</strong> parked
          </button>
        )}
        {/*
          "Summary", because it does not show all of anything.

          It was called "Show all" and opened an eight-tile dashboard above the capture box, seven
          tiles of it reading 0 or "—", then renamed itself "Hide summary" — so the control named
          itself one thing on the way in and another on the way out, and neither of them was what
          it did. A person expecting more tasks got fewer, pushed down the page.
        */}
        <button
          type="button"
          className="link overview-toggle"
          aria-expanded={false}
          onClick={() => setExpanded(true)}
        >
          Summary
        </button>
      </div>
    );
  }

  return (
    <div className="task-overview" data-testid="task-overview">
      <button
        type="button"
        className="link overview-toggle"
        aria-expanded={true}
        onClick={() => setExpanded(false)}
      >
        Hide summary
      </button>

      <section>
        <h4>By status</h4>
        {/*
          The tiles that stand for something, and a line for the rest.

          Eight tiles of which seven read 0 or "—" is not a summary; it is a form with nothing in
          it. The collapsed chips already drop zeros for exactly this reason, and opening the panel
          was undoing that — so a person who pressed it to learn more learned less, further down.
        */}
        <div className="stat-row">
          {(live.length > 0 ? live : TILE_LABELS).map((tile) => (
            <Tile
              key={tile.key}
              label={tile.label}
              value={String(byStatus[tile.key])}
              pressed={tileIsPressed(filters[tile.key], active)}
              onClick={() => onFilter(filters[tile.key])}
              hint={`Show ${tile.label.toLowerCase()}`}
            />
          ))}
          {byStatus.parked > 0 && (
            <Tile
              label={SHELF_LABEL}
              value={String(byStatus.parked)}
              pressed={tileIsPressed(filters.parked, active)}
              onClick={() => onFilter(filters.parked)}
              hint="Out of the list until somebody picks them up again — show them"
            />
          )}
        </div>
        {live.length > 0 && live.length < TILE_LABELS.length && (
          <p className="subtext">
            {TILE_LABELS.filter((t) => byStatus[t.key] === 0)
              .map((t) => t.label.toLowerCase())
              .join(', ')}
            : none.
          </p>
        )}
        {/*
          How bad the worst of it is, which the count alone does not say.

          "26 late" and "26 late, the oldest by 43 days" are different situations and the second is
          the one that decides what to do first. Here rather than under Complexity: it is a fact
          about the late set, which is the tile above it, and not about estimates.

          Absent when nothing is late, by the section's own rule — a zero is not a tile — and read
          from the server rather than derived here, so this and the Today banner cannot come to
          disagree about the same rows.
        */}
        {oldestLateDays !== null && (
          <div className="stat-row">
            <Tile
              label="Oldest late"
              value={`${oldestLateDays}d`}
              pressed={tileIsPressed(filters.late, active)}
              onClick={() => onFilter(filters.late)}
              hint="Days since the earliest passed deadline — or plan, where there is no deadline"
            />
          </div>
        )}
        {/*
          Which clock made them late — the same two figures Today's band says, off the same
          response, so the two screens cannot split one pile differently. Words rather than two
          more tiles: a tile is a press, and these are readings of the Late tile above, whose
          press opens both halves.
        */}
        {byStatus.late > 0 && (
          <p className="subtext" data-testid="late-split">
            Late: {latePileSentence(lateByDeadline, lateByPlan, null)}
          </p>
        )}
      </section>

      {/*
        The same rule as the section above it: a zero is not a tile.

        `By status` got this treatment and Complexity did not, so on an account that estimates
        nothing the panel still opened three tiles reading "0", "0" and "—". Half a rule applied is
        how a screen ends up inconsistent with itself one section apart.

        An account that has never estimated anything has no complexity to summarise, so the whole
        section goes rather than being drawn empty — which is also the honest answer to "how big is
        my work" when nothing carries a size.
      */}
      {estimatesAnything && (
        <section>
          <h4>Complexity</h4>
          <div className="stat-row">
            {complexity.openPoints > 0 && (
              <Tile
                label="Open points"
                value={String(complexity.openPoints)}
                hint="Estimated points on everything not done or dropped"
              />
            )}
            {complexity.donePointsThisWeek > 0 && (
              <Tile
                label="Done this week"
                value={String(complexity.donePointsThisWeek)}
                hint="Points completed since the start of this week"
              />
            )}
            {/*
                Absent, not a dash.

                This used to render "—" for null, on the reasoning that "nothing is estimated" and
                "everything is estimated at nothing" are different situations. Only the first can
                occur: `complexityPoints` carries a Fibonacci check constraint, so a zero estimate
                is refused at insert — a test in `taskOverview.e2e.spec.ts` pins that. So the dash
                only ever meant "nothing open is sized", which the tile's absence says already.
            */}
            {complexity.averagePoints !== null && (
              <Tile
                label="Avg per task"
                value={complexity.averagePoints.toFixed(1)}
                hint="Averaged over open tasks that carry an estimate"
              />
            )}
            {complexity.largestOpen && (
              <Tile
                label={complexity.largestOpen.title}
                value={`${complexity.largestOpen.points} pts`}
                hint="The largest open task — usually the one being avoided"
              />
            )}
          </div>
        </section>
      )}

      {used.length > 0 && (
        <section>
          <h4>From templates</h4>
          <div className="stat-row">
            {used.map((t) => (
              <Tile
                key={t.id}
                label={t.name}
                value={`${t.useCount}`}
                hint={`${t.name} has produced ${t.useCount} task${t.useCount === 1 ? '' : 's'}`}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
